import { useTranslation } from 'react-i18next';

import { dashboardLocale } from '../dashboardLocale';
import { FeedingKpiCard } from '../FeedingKpiCard/FeedingKpiCard';
import { formatTarget } from '../PenComparisonTable/penComparisonFormat';
import { systemLabels } from '../systemLabel';

import {
    cardQuality,
    formatNumber,
    type KpiCardProps,
    kpiWarningMarker,
    NO_BREAK,
} from './kpiCardFormat';
import { coverageNote } from './kpiCoverageNote';
import { footerTarget, type KpiTarget, kpiWarning, pensIn, resolveKpiTarget } from './kpiTargets';

export function RgiKpiCard(props: KpiCardProps) {
    const { summary, config, pens } = props;
    const { t, i18n } = useTranslation();
    const locale = dashboardLocale(i18n);
    // IPS 40: name the customer's own system rather than a generic "PMS".
    const system = systemLabels(t, config.pmsSystemName);
    // As on the bFCR card: each value against its own pens, the Target row over both.
    const targetOf = (ids: number[]): KpiTarget =>
        resolveKpiTarget(pensIn(pens, ids), config, 'rgi');
    const target = footerTarget(
        pens,
        [summary.coverage.rgiFeed, summary.coverage.rgiBioscope],
        config,
        'rgi'
    );
    // Named at the precision it is compared with: at 0 dp a target of 100.4 would read
    // "≥ 100" beside a value of 100.0 that is marked below it.
    const targetText =
        target.kind === 'shared'
            ? `≥${NO_BREAK}${formatTarget(target.target.target, 'rgi', locale)}`
            : t('feedingModule.kpi.targetRange', {
                  min: formatTarget(target.min, 'rgi', locale),
                  max: formatTarget(target.max, 'rgi', locale),
              });
    const { note, noteReasons } = coverageNote(
        [
            { metric: 'rgiFeed', label: system.name, namesSystem: true },
            { metric: 'rgiBioscope', label: t('feedingModule.bioscope') },
        ],
        props,
        t
    );

    // One decimal, as the API floors it: at 0 dp a 99.6 would print "100" beside a
    // warning that it is below 100 (F1).
    return (
        <FeedingKpiCard
            label={t('feedingModule.kpi.rgi')}
            quality={cardQuality(summary, summary.rgiBioscope != null)}
            values={[
                {
                    label: system.name,
                    text: formatNumber(summary.rgiFeed, 1, locale),
                    warning: kpiWarningMarker(
                        kpiWarning(
                            'rgiSystem',
                            summary.rgiFeed,
                            targetOf(summary.coverage.rgiFeed)
                        ),
                        system,
                        t,
                        locale
                    ),
                },
                {
                    label: t('feedingModule.bioscope'),
                    text: formatNumber(summary.rgiBioscope, 1, locale),
                    warning: kpiWarningMarker(
                        kpiWarning(
                            'rgiBioscope',
                            summary.rgiBioscope,
                            targetOf(summary.coverage.rgiBioscope)
                        ),
                        system,
                        t,
                        locale
                    ),
                },
            ]}
            details={[{ label: t('feedingModule.kpi.target'), value: targetText }]}
            note={note}
            noteReasons={noteReasons}
        />
    );
}
